/** @jsx jsx */
import { Heading, Stack } from "@eworkit/iris";
import { Formik, FormikHelpers, FormikProps } from "formik";
import i18n, { TSupportedLanguageKey } from "i18n";
import React, { useContext } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import { useRouter } from "router";
import { jsx } from "theme-ui";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { applyApiErrors } from "@/components/Formik";
import { registerUser } from "@/data/session";
import { MessagesContext } from "@/eworkitComponents";
import { useRecaptcha } from "@/infrastructure/Recaptcha";
import { ACTION_NAMES } from "@/infrastructure/Recaptcha/types";
import {
  FORM_EMPTY_INITIAL_VALUES,
  FORM_MESSENGER_CONTEXT,
  ISignUpUserFormValues,
  SignUpUserForm,
  validationSchema,
} from "@/views/components/Forms/SignUpUserForm";
import { router } from "@/views/router";

interface ISessionSignUpUserProps {
  jobRequestId?: number;
}

export const SessionSignUpUser: React.FunctionComponent<ISessionSignUpUserProps> = React.memo(({ jobRequestId }) => {
  const { t } = useTranslation(["public", "common"]);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { addMessage } = useContext(MessagesContext);
  const { sendEvent } = useAnalytics();

  const { searchParams, push } = useRouter();

  const handleSubmit = async (values: ISignUpUserFormValues, actions: FormikHelpers<ISignUpUserFormValues>) => {
    try {
      const recaptchaToken = useRecaptcha && executeRecaptcha ? await executeRecaptcha(ACTION_NAMES.register) : "";
      const registeredUser = await registerUser(
        { recaptchaToken },
        {
          ...values,
          language: i18n.language as TSupportedLanguageKey,
          onboardingJobRequestId: jobRequestId,
        },
      );

      sendEvent({ type: "sign_up" });

      push(router.get("register-email-verify").path, { email: registeredUser.email });
    } catch (response) {
      addMessage({
        text: t("common:VALIDATION_MESSAGES.GENERAL"),
        type: "warning",
        context: FORM_MESSENGER_CONTEXT,
      });

      applyApiErrors(actions, response.payload);
      actions.setSubmitting(false);
    }
  };

  const renderForm = (formikProps: FormikProps<ISignUpUserFormValues>) => <SignUpUserForm form={formikProps} />;

  const initialValues: any = searchParams ?? {};

  return (
    <Stack gap="md">
      <Heading size="h-md" align="center">
        {t("SESSION_SIGNUP.USER_FORM.REGISTER")}
      </Heading>

      <Formik<ISignUpUserFormValues>
        initialValues={{ ...FORM_EMPTY_INITIAL_VALUES, ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {props => renderForm(props)}
      </Formik>
    </Stack>
  );
});
